import React, { ChangeEvent, useEffect, useState } from 'react';
import { TextField as CustomTextField } from '@mui/material';
import moment from 'moment';
import { Button, Col, Row } from 'react-bootstrap';
import { DateRangePicker, RangeKeyDict } from 'react-date-range';
import { useTranslation } from 'react-i18next';
import { AiFillInfoCircle } from 'react-icons/ai';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { PackagesEnum } from '../../../../../enum/PackageEnum';
import { ITimeline } from '../../../../../model/briefing/step3/ITimeline';
import { IParams } from '../../../../../model/shared/IParams';
import { InfoBox } from '../../../../../shared/components/alert/InfoBox';
import CustomDatePicker from '../../../../../shared/components/DatePicker';
import ToggleSwitch from '../../../../../shared/components/ToggleSwitch';
import {
    formatDate,
    getLocalDateWithoutTimezone,
    isDateLessThan7Days,
    oneStepUp,
} from '../../../../../shared/functions/Functions';
import { IAllStates } from '../../../../../store/rootReducer';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../../utils/toasters';
import TooltipPackage from '../../../../shared/packages/components/TooltipPackage';
import { BriefingService } from '../service';
import { CampaignOverviewCard } from './shared/CampaignOverviewCard';
import { ProgressBarCampaign } from './shared/ProgressBarCampaign';
import { StepperRouting } from './shared/StepperRouting';
import ClickOutsideDetector from '../../../../../shared/components/ClickOutsideDetector';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { CampaignValidationStatusEnum } from '../../../../../enum/CampaignValidationStatusEnum';
import StepThreeLongTermComponent from './StepThreeLongTermComponent';
import { InvoicePeriodEnum } from '../../../../../enum/InvoicePeriodEnum';
import { CampaignDurationEnum } from '../../../../../enum/CampaignDurationEnum';

export const StepThree = () => {
    const { company } = useSelector((state: IAllStates) => state.auth.user);
    const [isNextClicked, setIsNextClicked] = useState(false);
    const [isDatepickerShow, setIsDatepickerShow] = useState(false);
    const [thirdStep, setThirdStep] = useState<ITimeline>({
        id: 0,
        date: '',
        publishFrom: '',
        publishTo: '',
        isReviewed: true,
        reviewDays: 0,
        postingDeadline: '',
        status: '',
    });
    const { id } = useParams<keyof IParams>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isLongTermCampaign = thirdStep?.isLongTerm;
    const closeDataPicker = () => {
        setIsDatepickerShow(false);
    };
    const handleChangeTimepicker = (newValue: Date | null, name: string) => {
        const value = getLocalDateWithoutTimezone(newValue as Date);
        setThirdStep((prev: ITimeline) => ({ ...prev, [name]: value ?? prev[name as keyof ITimeline] }));
    };

    const toggleHandler = () => {
        setThirdStep((prev: ITimeline) => ({
            ...prev,
            isReviewed: !prev.isReviewed,
            reviewDays: prev.isReviewed ? 0 : prev.reviewDays,
        }));
    };
    const skipReportingHandler = () => {
        setThirdStep((prev: ITimeline) => ({ ...prev, canSkipReporting: !prev.canSkipReporting }));
    };
    const handleChangeDeadline = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (thirdStep.isReviewed) {
            setThirdStep((prev: ITimeline) => ({ ...prev, [e.target.name]: e.target.value }));
        }
    };

    const updateStepThree = () => {
        if (thirdStep.isReviewed && thirdStep.reviewDays === 0 && !isLongTermCampaign) {
            return WarningToast(t('general.alert.warning_deadline'));
        }
        const dateWithoutTime = new Date(thirdStep.date).toISOString()?.split('T')[0];
        const publishFromWithoutTime = new Date(thirdStep.publishFrom).toISOString()?.split('T')[0];
        const showWarningApplicationDeadline = moment(publishFromWithoutTime).isBefore(dateWithoutTime);
        if (showWarningApplicationDeadline && !isLongTermCampaign) {
            return WarningToast(t('general.alert.warning_application_period'));
        }
        const publishToDeadline = new Date(thirdStep.publishTo);
        const reportingDeadline = new Date(thirdStep.postingDeadline);

        if (isDateLessThan7Days(reportingDeadline, publishToDeadline) && !isLongTermCampaign) {
            return WarningToast(t('general.alert.warning_days'));
        }
        if (!thirdStep?.postingDeadline) {
            return;
        }
        setIsNextClicked(true);
        BriefingService.updateStepThree(thirdStep, id)
            .then(response => {
                if (response) {
                    oneStepUp(navigate, id ?? '', response.isBriefingComplete, response.briefingStep);
                    SuccessToast(t('general.alert.success_updated_campaign_step'));
                }
            })
            .catch(error => ErrorToast(error))
            .finally(() => {
                setIsNextClicked(false);
            });
    };

    const changeDatePickerHandler = (event: RangeKeyDict) => {
        const { range1 } = event;
        const startDate = getLocalDateWithoutTimezone(range1?.startDate as Date);
        const endDate = getLocalDateWithoutTimezone(range1?.endDate as Date);
        if (range1?.startDate?.toISOString() !== range1?.endDate?.toISOString()) {
            if (calculatePublicationLength(startDate, endDate, company?.package?.publicationPeriodLength ?? 1)) {
                return WarningToast(
                    t('general.alert.nb_weeks_limit', { nbWeeks: company?.package?.publicationPeriodLength ?? 1 })
                );
            }
        }
        setThirdStep(prev => ({
            ...prev,
            publishFrom: startDate,
            publishTo: endDate,
            postingDeadline: new Date(endDate.getTime() + 7 * 24 * 60 * 60 * 1000),
        }));
        if (range1?.startDate?.toISOString() !== range1?.endDate?.toISOString()) {
            // close window if we select date
            closeDataPicker();
        }
    };

    const calculatePublicationLength = (startDate: Date, endDate: Date, publicationPeriodLength: number) => {
        const maxWeek = publicationPeriodLength > 105 ? 105 : publicationPeriodLength;
        const maxDays = maxWeek * 7; // calculate max date with number of weeks
        const timeDifference = endDate.getTime() - startDate.getTime();
        const daysDifference = timeDifference / (1000 * 60 * 60 * 24);
        if (daysDifference > maxDays) {
            return true;
        }
    };

    useEffect(() => {
        BriefingService.getBriefingStepThree(id).then(response => {
            const isFirstEnter = !response?.date;
            setThirdStep(
                !isFirstEnter
                    ? response
                    : {
                          ...response,
                          reviewDays: 5,
                          isReviewed: true,
                          publishFrom: new Date(),
                          publishTo: new Date(),
                          postingDeadline: moment().add(7, 'days').toDate(),
                          date: moment().toDate(),
                          invoiceDuration: CampaignDurationEnum.MONTH_1,
                          invoicePeriod: InvoicePeriodEnum.MONTHLY,
                      }
            );
        });
    }, []);

    const multipleSelectHandler = (paramKey: string, paramValue: any) => {
        setThirdStep(prev => ({ ...prev, [paramKey]: paramValue }));
    };

    return (
        <Row className="mb-5 pb-5">
            <Col md={8}>
                {!thirdStep.isBriefingComplete ? (
                    <div className="mb-4">
                        <ProgressBarCampaign title={t('brand.campaign.briefing.step3.timeline')} percent={15} />
                    </div>
                ) : null}
                <CardLayout>
                    <div className="d-flex flex-column flex-md-row">
                        {isLongTermCampaign ? (
                            <StepThreeLongTermComponent
                                handleChangeTimepicker={handleChangeTimepicker}
                                date={thirdStep.date}
                                invoicePeriod={thirdStep.invoicePeriod}
                                invoiceDuration={thirdStep.invoiceDuration}
                                isApproved={thirdStep.approvalStatus === CampaignValidationStatusEnum.APPROVED}
                                multipleSelectHandler={multipleSelectHandler}
                            />
                        ) : (
                            <div className="w-md-75">
                                <div className="d-flex flex-column">
                                    <div className="mb-1">
                                        <h6 className="font-weight-bold mb-0 ">
                                            {t('brand.campaign.briefing.step3.application_deadline')}
                                        </h6>
                                        <span className="text-muted">
                                            {t('brand.campaign.briefing.step3.application_deadline_description')}
                                        </span>
                                    </div>
                                    <div className="w-100 d-flex align-items-center my-2">
                                        <div className="w-md-50">
                                            <CustomDatePicker
                                                label={t('brand.campaign.briefing.step3.apply_until')}
                                                value={thirdStep?.date}
                                                // disableTo={Boolean(thirdStep?.publishFrom) ? removeTimeZoneFromDate(new Date(thirdStep?.publishFrom).toISOString() ?? '') : undefined}
                                                disableFrom={new Date()}
                                                onChange={e => handleChangeTimepicker(e, 'date')}
                                            />
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-start flex-column my-3">
                                        <h6 className="font-weight-bold">
                                            {t('brand.campaign.briefing.step3.publication_period')}
                                        </h6>
                                        <div className="d-flex flex-column w-100">
                                            <div className="d-flex align-items-center">
                                                <span className="text-muted">
                                                    {t('brand.campaign.briefing.step3.from')} -{' '}
                                                    {t('brand.campaign.briefing.step3.to')}
                                                </span>
                                                {company?.package?.name === PackagesEnum.BASIC && (
                                                    <TooltipPackage
                                                        placement="bottom"
                                                        description={'limitPublication'}
                                                        trigger="click"
                                                        show={company?.package?.name === PackagesEnum.BASIC}
                                                    >
                                                        <span className="cursor-pointer">
                                                            <AiFillInfoCircle size={15} className="ml-1" />
                                                        </span>
                                                    </TooltipPackage>
                                                )}
                                            </div>
                                            {thirdStep && (
                                                <ClickOutsideDetector onOutsideClick={closeDataPicker}>
                                                    <div className="position-relative">
                                                        <div className="d-flex align-items-center">
                                                            <input
                                                                type="text"
                                                                id="date"
                                                                className="w-md-50 form-control"
                                                                style={{ minHeight: 37 }}
                                                                onClick={() => setIsDatepickerShow(prev => !prev)}
                                                                value={`${formatDate(
                                                                    thirdStep?.publishFrom
                                                                )} - ${formatDate(thirdStep?.publishTo)}`}
                                                            />
                                                            {isDatepickerShow && (
                                                                <Button
                                                                    onClick={closeDataPicker}
                                                                    variant="outline-primary"
                                                                    className="ml-2"
                                                                >
                                                                    {t('general.button.cancel')}
                                                                </Button>
                                                            )}
                                                        </div>
                                                        {isDatepickerShow && (
                                                            <DateRangePicker
                                                                onChange={changeDatePickerHandler}
                                                                months={2}
                                                                ranges={[
                                                                    {
                                                                        startDate: new Date(thirdStep?.publishFrom),
                                                                        endDate: new Date(thirdStep?.publishTo),
                                                                    },
                                                                ]}
                                                                rangeColors={['var(--primary)']}
                                                                minDate={new Date(thirdStep?.date)}
                                                                // maxDate={calculatePublicationLength(company?.package.publicationPeriodLength)}
                                                                className="position-absolute z-index-2 box-shadow default-radius"
                                                                direction="horizontal"
                                                            />
                                                        )}
                                                    </div>
                                                </ClickOutsideDetector>
                                            )}
                                        </div>
                                    </div>

                                    <h6 className="font-weight-bold mb-0 mt-2">
                                        {t('brand.campaign.briefing.step3.approval_deadline')}
                                    </h6>
                                    <span className="text-muted">
                                        {t('brand.campaign.briefing.step3.approval_deadline_description')}
                                    </span>
                                </div>
                                <div className="d-flex align-items-lg-center flex-column flex-md-row my-3">
                                    <div className="mb-3 mb-md-0">
                                        <ToggleSwitch
                                            small={true}
                                            id="content-approval"
                                            onChange={toggleHandler}
                                            checked={thirdStep?.isReviewed}
                                        />
                                    </div>
                                    <CustomTextField
                                        onChange={handleChangeDeadline}
                                        type="number"
                                        name="reviewDays"
                                        focused
                                        label={t('brand.campaign.briefing.step3.days_before_scheduled_posting')}
                                        variant="outlined"
                                        value={thirdStep?.reviewDays}
                                        className={`w-md-50 ml-md-2 ${!thirdStep.isReviewed ? 'invisible' : ''}`}
                                        size="small"
                                        InputProps={{ inputProps: { min: 0 } }}
                                        onKeyPress={event =>
                                            (event?.key === '-' || event?.key === '+') && event.preventDefault()
                                        }
                                    />
                                </div>
                                <div>
                                    <div className="mt-4 mb-2">
                                        <h6 className="font-weight-bold mb-0">
                                            {t('brand.campaign.briefing.step3.postingDeadline')}
                                        </h6>
                                        <span className="text-muted">
                                            {t('brand.campaign.briefing.step3.postingDeadlineDescription')}
                                        </span>
                                    </div>
                                    <div className="w-100 d-flex align-items-center">
                                        <div className="w-md-50">
                                            <CustomDatePicker
                                                label={t('brand.campaign.briefing.step3.postingDeadline')}
                                                value={thirdStep?.postingDeadline as string}
                                                disabled={true}
                                                onChange={e => handleChangeTimepicker(e, 'postingDeadline')}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {company?.canSkipReporting && (
                                    <div className="d-flex align-items-start mt-4">
                                        <div className="mr-2">
                                            <ToggleSwitch
                                                small={true}
                                                id="can-skip-reporting"
                                                onChange={skipReportingHandler}
                                                checked={!!thirdStep?.canSkipReporting}
                                            />
                                        </div>
                                        <div>
                                            <h6 className="font-weight-bold mb-0">
                                                {t('brand.campaign.briefing.step3.canSkipReporting')}
                                            </h6>
                                            <span className="text-muted">
                                                {t('brand.campaign.briefing.step3.canSkipReportingDescription')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                <div className="mt-3 font-weight-semi-bold ">
                                    <span>{t('brand.campaign.briefing.step3.pending_rejected')}</span>
                                    <span>{t('brand.campaign.briefing.step3.applicationDeadline')}</span>
                                </div>
                            </div>
                        )}
                        <div className="mt-3 mt-md-0 ml-lg-3 w-md-50">
                            <InfoBox
                                variant="info"
                                title={
                                    isLongTermCampaign
                                        ? t('brand.campaign.briefing.step3.campaign_duration')
                                        : t('brand.campaign.briefing.step3.influence_vision_feedback')
                                }
                                text={
                                    isLongTermCampaign
                                        ? t('brand.campaign.briefing.step3.campaign_duration_explanation')
                                        : t('brand.campaign.briefing.step3.iv_feedback_publication')
                                }
                                buttonName=""
                            />
                            <InfoBox
                                variant="info"
                                title={
                                    isLongTermCampaign
                                        ? t('brand.campaign.briefing.step3.payout_method')
                                        : t('brand.campaign.briefing.step3.influence_vision_feedback')
                                }
                                text={
                                    isLongTermCampaign
                                        ? t('brand.campaign.briefing.step3.payout_method_explanation')
                                        : t('brand.campaign.briefing.step3.iv_feedback_content')
                                }
                                buttonName=""
                            />
                        </div>
                    </div>
                    <div>
                        <StepperRouting
                            submitStepHandler={updateStepThree}
                            isNextDisabled={isNextClicked}
                            status={thirdStep?.status}
                        />
                    </div>
                </CardLayout>
            </Col>
            <Col md={4} className="pr-md-4">
                <CampaignOverviewCard />
            </Col>
        </Row>
    );
};
